import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Box, Paper, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import type { RootState } from "../../store/Index";
import { Button, Skeleton } from "../../components/ui";
import { useThemeColors } from "../../hooks/useThemeColors";
import { openJobCard } from "./sso";
import { DEFAULT_STATUS, JobCardQueue, fetchCount, isQueue } from "./jobCardApi";
import QueueTable from "./QueueTable";
import DetailModal from "./DetailModal";
import CreateAssignmentModal from "./CreateAssignmentModal";

const REVIEWER_GROUPS = ["job-card-admin", "job-card-supervisor"];

const ROLE_COPY: Record<string, string> = {
  "job-card-admin": "Review proposals, assignments, tasks and reports, or continue to the Job Card app.",
  "job-card-supervisor":
    "Review your interns' work, or continue to the Job Card app.",
  "job-card-finance": "Continue to the Job Card app to work on payroll.",
  "job-card-user": "Continue to the Job Card app to log your work.",
};

interface StatProps {
  title: string;
  value: number | null;
  color: string;
  icon: React.ElementType;
}

const Stat = ({ title, value, color, icon: Icon }: StatProps) => {
  const colors = useThemeColors();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "12px",
        border: `1px solid ${color}30`,
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          backgroundColor: `${color}20`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color, fontSize: 24 }} />
      </Box>
      <Box>
        {value === null ? (
          <Skeleton variant="text" width={36} height={34} />
        ) : (
          <Typography
            sx={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.1, color: colors.primary[100] }}
          >
            {value}
          </Typography>
        )}
        <Typography sx={{ fontSize: "0.8rem", color: colors.primary[300] }}>{title}</Typography>
      </Box>
    </Paper>
  );
};

const QUEUES: { queue: JobCardQueue; label: string }[] = [
  { queue: "proposal", label: "Proposals" },
  { queue: "assignment", label: "Assignments" },
  { queue: "task", label: "3 Things" },
  { queue: "report", label: "Reports" },
];

const QueueList = ({
  active,
  counts,
  onSelect,
}: {
  active: JobCardQueue;
  counts: Counts | null;
  onSelect: (q: JobCardQueue) => void;
}) => {
  const colors = useThemeColors();
  return (
    <Paper
      elevation={0}
      sx={{ backgroundColor: colors.primary[900], borderRadius: "12px", padding: "20px" }}
    >
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: colors.primary[100], mb: 1.5 }}>
        Show
      </Typography>
      {QUEUES.map(({ queue, label }) => {
        const isActive = queue === active;
        return (
          <Box
            key={queue}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(queue)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(queue)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1.5,
              py: 1,
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "0.88rem",
              fontWeight: isActive ? 700 : 400,
              color: colors.primary[100],
              backgroundColor: isActive ? colors.primary[800] : "transparent",
              "&:hover": { backgroundColor: colors.primary[800] },
            }}
          >
            <span>{label}</span>
            <Typography component="span" sx={{ fontSize: "0.8rem", color: colors.primary[300] }}>
              {counts?.[queue] ?? "-"}
            </Typography>
          </Box>
        );
      })}
    </Paper>
  );
};

const statGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 2,
  mb: 3,
};

type Counts = Record<"proposal" | "assignment" | "task" | "report" | "inProgress", number>;

const JobCard = () => {
  const colors = useThemeColors();
  const user = useSelector((state: RootState) => state.auth.user);
  const [params, setParams] = useSearchParams();

  const groups = user?.group_names ?? [];
  const isReviewer = groups.some((g) => REVIEWER_GROUPS.includes(g));
  const primaryGroup =
    ["job-card-admin", "job-card-supervisor", "job-card-finance", "job-card-user"].find((g) =>
      groups.includes(g),
    ) ?? "";

  // The selected queue and filter live in the URL so BACK from a detail page
  // returns to the same list.
  const rawQueue = params.get("queue");
  const queue: JobCardQueue = isQueue(rawQueue) ? rawQueue : "proposal";
  const rawStatus = params.get("status");
  const status = rawStatus === null ? DEFAULT_STATUS[queue] : rawStatus;

  const selectQueue = (q: JobCardQueue) =>
    setParams({ queue: q, status: DEFAULT_STATUS[q] }, { replace: true });
  const selectStatus = (s: string) => setParams({ queue, status: s }, { replace: true });

  const [counts, setCounts] = useState<Counts | null>(null);
  const [countsError, setCountsError] = useState(false);

  const [open, setOpen] = useState<{ queue: JobCardQueue; id: number } | null>(null);
  const [creating, setCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const itemId = Number(params.get("item"));
    if (isQueue(rawQueue) && Number.isInteger(itemId) && itemId > 0) {
      setOpen({ queue: rawQueue, id: itemId });
    }
  }, [params, rawQueue]);

  const loadCounts = useCallback(async () => {
    try {
      const [proposal, assignment, task, report, inProgress] = await Promise.all([
        fetchCount("proposal", DEFAULT_STATUS.proposal),
        fetchCount("assignment", DEFAULT_STATUS.assignment),
        fetchCount("task", DEFAULT_STATUS.task),
        fetchCount("report", DEFAULT_STATUS.report),
        fetchCount("assignment", "accepted"),
      ]);
      setCounts({ proposal, assignment, task, report, inProgress });
      setCountsError(false);
    } catch {
      setCounts(null);
      setCountsError(true);
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const changed = () => {
    setRefreshKey((k) => k + 1);
    loadCounts();
  };

  const closeDetail = () => {
    setOpen(null);
    if (params.has("item")) {
      const nextParams = new URLSearchParams(params);
      nextParams.delete("item");
      setParams(nextParams, { replace: true });
    }
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: colors.primary[100] }}>
            Job Card
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", color: colors.primary[300], mt: 0.25 }}>
            {ROLE_COPY[primaryGroup] ?? "Continue to the Job Card app."}
          </Typography>
        </Box>
        {isReviewer && (
          <Button
            onClick={() => setCreating(true)}
            startIcon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
          >
            Create assignment
          </Button>
        )}
        <Button onClick={openJobCard} endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}>
          Open Job Card App
        </Button>
      </Box>

      {countsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Could not load Job Card dashboard counts. Please try again.
        </Alert>
      )}

      {isReviewer ? (
        <>
          <Box sx={statGrid}>
            <Stat
              title="Proposals to review"
              value={counts?.proposal ?? null}
              color="#2563EB"
              icon={RateReviewOutlinedIcon}
            />
            <Stat
              title="Assignments to review"
              value={counts?.assignment ?? null}
              color="#059669"
              icon={AssignmentTurnedInOutlinedIcon}
            />
            <Stat
              title="Reports in draft"
              value={counts?.report ?? null}
              color="#7C3AED"
              icon={SummarizeOutlinedIcon}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 240px" },
              gap: 3,
              alignItems: "start",
            }}
          >
            <Box sx={{ order: { xs: 2, lg: 1 }, minWidth: 0 }}>
              <QueueTable
                queue={queue}
                status={status}
                onStatusChange={selectStatus}
                onOpen={(q, id) => setOpen({ queue: q, id })}
                refreshKey={refreshKey}
              />
            </Box>
            <Box sx={{ order: { xs: 1, lg: 2 } }}>
              <QueueList active={queue} counts={counts} onSelect={selectQueue} />
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={statGrid}>
          <Stat
            title="Proposals awaiting review"
            value={counts?.proposal ?? null}
            color="#2563EB"
            icon={RateReviewOutlinedIcon}
          />
          <Stat
            title="Assignments in progress"
            value={counts?.inProgress ?? null}
            color="#D97706"
            icon={HourglassEmptyOutlinedIcon}
          />
        </Box>
      )}
      <DetailModal target={open} onClose={closeDetail} onChanged={changed} />
      <CreateAssignmentModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setParams({ queue: "assignment", status: "sent" }, { replace: true });
          changed();
        }}
      />
    </Box>
  );
};

export default JobCard;
